import React, { useState } from 'react';

import { Box, LinearProgress, Typography } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { ReliasTraining } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { ExpandableDashboardActionItem, LightChip } from '@uplift/ui-v2';

import { getAggregateDisplayStatus, getCompletedCount, TrainingAggregateStatus } from './helpers';
import { TrainingRow } from './TrainingRow';

export type RequiredTrainingsCardProps = {
  trainings: ReliasTraining[];
  lastSyncedAt: string | null;
};

const AGGREGATE_STATUS_CHIP_COLOR: Record<TrainingAggregateStatus, 'successLight' | 'info' | 'default'> = {
  completed: 'successLight',
  inProgress: 'info',
  notStarted: 'default',
};

// Shared between the onboarding "Getting Started" modal and the ongoing main-dashboard To Do
// widget — both surfaces show the same parent-with-child-rows design for required trainings,
// just inside different lists. Callers are responsible for the "should this show at all" and
// empty-state logic, since that differs per surface.
export const RequiredTrainingsCard: React.VFC<RequiredTrainingsCardProps> = ({ trainings, lastSyncedAt }) => {
  const l10n = useL10n();
  // Passing `true` forces the item open on mount; handing control back to the component's own
  // internal (collapsed-by-default) state on the first toggle is what lets it re-expand afterward.
  const [ isExpanded, setIsExpanded ] = useState<boolean | undefined>(true);
  const completedCount = getCompletedCount(trainings);
  const aggregateStatus = getAggregateDisplayStatus(trainings);
  const aggregateChipColor = AGGREGATE_STATUS_CHIP_COLOR[aggregateStatus];

  return (
    <ExpandableDashboardActionItem
      icon={<SchoolOutlinedIcon />}
      color="primary"
      title={l10n.dashboard.actionItems.requiredTrainings.title}
      badge={(
        <LightChip
          size="small"
          label={l10n.dashboard.actionItems.requiredTrainings.aggregateStatus[aggregateStatus]}
          color={aggregateChipColor}
        />
      )}
      body={(
        <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
          <Typography variant="body2" color="textSecondary">
            {l10n.dashboard.actionItems.requiredTrainings.progress(completedCount, trainings.length)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(completedCount / trainings.length) * 100}
            color="secondary"
          />
          {lastSyncedAt && (
            <Typography variant="caption" color="textSecondary">
              {l10n.dashboard.actionItems.requiredTrainings.lastSynced(CalendarDateService.date(lastSyncedAt).fromNow())}
            </Typography>
          )}
        </Box>
      )}
      expandText={l10n.dashboard.actionItems.requiredTrainings.showTrainings}
      hideText={l10n.dashboard.actionItems.requiredTrainings.hideTrainings}
      isExpanded={isExpanded}
      onExpandedChange={() => setIsExpanded(undefined)}
    >
      <Box display="flex" flexDirection="column">
        {trainings.map((training) => (
          <TrainingRow key={training.id} training={training} />
        ))}
      </Box>
    </ExpandableDashboardActionItem>
  );
};
